import type { ReactNode } from 'react'

import { motion } from 'framer-motion'

import { ActivePanelType } from './types'

interface AnimatedPanelProps {
  children: ReactNode
  panelType: ActivePanelType.Bid | ActivePanelType.Ask
  activePanel: ActivePanelType
  className?: string
}

/**
 * AnimatedPanel provides smooth animations for order book panel transitions
 * using Framer Motion instead of CSS transitions for better performance and control.
 */
export function AnimatedPanel({
  children,
  panelType,
  activePanel,
  className = '',
}: AnimatedPanelProps) {
  const isVisible =
    activePanel === panelType || activePanel === ActivePanelType.Both

  // Animation variants for different states
  const variants = {
    hidden: {
      opacity: 0,
      flex: 0,
      width: 0,
    },
    visible: {
      opacity: 1,
      flex: 1,
      width: 1,
    },
  }

  return (
    <motion.div
      className={className}
      data-visible={isVisible}
      variants={variants}
      initial={false} // Don't animate on initial render
      animate={isVisible ? 'visible' : 'hidden'}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
        opacity: { duration: 0.2, ease: 'easeInOut' },
      }}
      style={{
        overflow: 'hidden',
        scrollbarWidth: 'none',
        willChange: 'flex, opacity, width', // Optimize for animation
        alignSelf: 'stretch', // Maintain height consistency
        minHeight: 0, // Prevent height bouncing
        height: '100%', // Ensure consistent height during transitions
        maxHeight: '100%', // Prevent expansion beyond container
      }}
    >
      {/* Inner content with separate opacity animation for smoother transitions */}
      <motion.div
        animate={{
          opacity: isVisible ? 1 : 0,
        }}
        transition={{
          duration: 0.15,
          delay: isVisible ? 0.1 : 0, // Slight delay when showing for smoother effect
          ease: 'easeInOut',
        }}
      >
        {children}
      </motion.div>
    </motion.div>
  )
}
